import * as ExpenseCalculators from 'app/calcs/expenses'
import { AbstractWhen, getDateOf, lastPersonLiving } from 'app/calcs/predicates'
import { expenseSummaryChartConfig } from 'app/containers/PipelineSection/Expenses/Summary/chart'
import { IQuestionsGroupContainerProps, QuestionsGroupContainer } from 'app/containers/PipelineSection/QuestionGroupContainer';
import { dateAtAge, deadBy, isValidPerson, Profile } from 'app/redux/Profile/model'
import * as utils from 'app/utils'
import * as _ from 'lodash'
import * as moment from 'moment'
import * as React from 'react'
import * as ReactHighcharts from 'react-highcharts'

interface IExpensesSummaryContainerState {
  profile: Profile,
  haveEnoughDataForChart: boolean,
  expensesText: string,
  expensesByGroupAndDate: ExpenseCalculators.ExpenseGroupValues[],
  importantDates: any[]
}

const currency = { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }

function funky(z: number) {
  return this.who + ', your expenses will be ' + z.toLocaleString('en', currency) + '.'
}

const makeEntry = (who: string, when: moment.Moment, fmt?: (x: number) => string) => ({ who, when, fmt: fmt || funky })

class ExpensesSummaryContainer extends QuestionsGroupContainer<IQuestionsGroupContainerProps, IExpensesSummaryContainerState> {
  state: IExpensesSummaryContainerState = {
    profile: null,
    haveEnoughDataForChart: false,
    expensesText: 'we have nothing to say',
    expensesByGroupAndDate: [],
    importantDates: []
  }

  componentWillMount() {
    this.onAppStateUpdate((appState) => {
      this.state.profile = appState.Profile.get('info') || null
      let expensesText = ''
      let expensesByGroupAndDate: ExpenseCalculators.ExpenseGroupValues[] = []
      const importantPeople = this.state.profile.getImportantPeople()
      let importantDates = []

      // generate the chart if we have enough information for that
      this.state.haveEnoughDataForChart = ExpenseCalculators.haveEnoughDataForExpensesCalculation(importantPeople)
      if (this.state.haveEnoughDataForChart) {
        const spouse = this.state.profile.getSpouse()

        // there are several important dates where we want to collect expenses for
        // now
        // the date that expenses switchover
        // if there is a spouse, their retirement date
        // the date that the last person dies
        importantDates = [
          makeEntry('now', utils.todaysDate(), (z: number) => 'Your current expenses on a monthly basis are ' + z.toLocaleString('en', currency) + '.'),
          //makeEntry('After ' + member.firstName + ' turns ' + utils.toNiceAge(this.state.profile.expenses.expenseSwitchoverMemberAge),
          makeEntry('During retirement',
            getDateOf(AbstractWhen.expenseSwitchover, this.state.profile).when)
        ]

        // if there are two people, add a date for the first death
        if (isValidPerson(spouse)) {
          const firstDeath = getDateOf(AbstractWhen.firstPersonDying, this.state.profile)
          const who = 'After ' + firstDeath.person.firstName + ' dies'
          importantDates.push(makeEntry(who, firstDeath.when))
        }

        expensesByGroupAndDate = importantDates.map((q) => ExpenseCalculators.getAllExpensesGrouped(this.state.profile, q.when))
        // tslint:disable-next-line:no-console
        console.log('expenses by group: ' + JSON.stringify(expensesByGroupAndDate, undefined, 4))

        // now, get the expenses for each of these dates, format them into text and accumulate the chart data
        const lastLivingWhen = getDateOf(AbstractWhen.lastPersonDying, this.state.profile).when
        const expenseBlurbs = []
        expenseSummaryChartConfig.series[0].data = importantDates
          .map((d) => {
            const expenses = ExpenseCalculators.getAllExpenses(this.state.profile, d.when)
            // tslint:disable-next-line:no-console
            console.log('important date for ' + d.who + ' on ' + d.when.format() + ' are ' + JSON.stringify(expenses, undefined, 4))
            const expensesSum = _.reduce(expenses, (sum, z) => sum + z.value, 0)
            expenseBlurbs.push(d.fmt(expensesSum))
            // tslint:disable-next-line:no-console
            console.log('expenses for ' + d.who + ' as of ' + d.when.format() + ' are ' + expensesSum)
            return [d.when.year(), expensesSum]
          })
          .concat([ [ lastLivingWhen.year(), 0 ] ])
        expenseSummaryChartConfig.xAxis.max = lastLivingWhen.year()
        expensesText = expenseBlurbs.join(' ')
      }

      this.setState({ profile: this.state.profile, expensesText, expensesByGroupAndDate, importantDates })
    })
  }

  render() {
    if (!this.state.profile) {
      return null
    }

    if (this.state.haveEnoughDataForChart) {
      return (
        <section className="expensesSummary">
          <h1><i className="fa fa-fw fa-balance-scale"></i> Expenses Summary</h1>

          <h4 className="lead"
            dangerouslySetInnerHTML={{ __html: this.state.expensesText }} />
          <p><small>(<b>Tip:</b> You can hover over data points to get more info.)</small></p>
          <div className="well"><ReactHighcharts config={expenseSummaryChartConfig} /></div>

          <br />
          <div className="table-responsive">
            <table className="table table-hover">
              <thead>
                <tr>
                  <td></td>
                  <td><em>Initial Expenses</em></td>
                  <td><em>Expenses After Change Age</em></td>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><a href="#/expenses/home_expenses/mortgage_rent">Home Expenses</a></td>
                  <td>{this.getExpenseFor(0, 'home')}</td>
                  <td>{this.getExpenseFor(1, 'home')}</td>
                </tr>
                <tr>
                  <td><a href="#/expenses/auto">Auto Monthly Payment</a></td>
                  <td>{this.getExpenseFor(0, 'auto')}</td>
                  <td>{this.getExpenseFor(1, 'auto')}</td>
                </tr>
                <tr>
                  <td><a href="#/expenses/food/groceries">Food</a></td>
                  <td>{this.getExpenseFor(0, 'food')}</td>
                  <td>{this.getExpenseFor(1, 'food')}</td>
                </tr>
                <tr>
                  <td><a href="#/expenses/clothing___personal/clothing">Clothing &amp; Personal</a></td>
                  <td>{this.getExpenseFor(0, 'clothingAndPersonal')}</td>
                  <td>{this.getExpenseFor(1, 'clothingAndPersonal')}</td>
                </tr>
                <tr>
                  <td><a href="#/expenses/entertainment/travel">Entertainment</a></td>
                  <td>{this.getExpenseFor(0, 'entertainment')}</td>
                  <td>{this.getExpenseFor(1, 'entertainment')}</td>
                </tr>
                <tr>
                  <td><a href="#/expenses/other/charity">Other</a></td>
                  <td>{this.getExpenseFor(0, 'other')}</td>
                  <td>{this.getExpenseFor(1, 'other')}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <br />
          <a href="#/dashboard" className="backtodash-link">
            <i className="fa fa-fw fa-arrow-left" />
            &nbsp;Back to Dashboard
          </a>
        </section>
      )
    } else {
      return (
        <section>
          <h1><i className="fa fa-fw fa-balance-scale"></i> Expenses Summary</h1>

          <p className="lead">Here is where you would normally find a chart showing of all the expenses for you and your spouse.</p>
          <p className="lead">
            However, you haven't completed enough information in your profile. Please update your profile and enter at least:
            <br /><br />
            <ul>
              <li>date of birth</li>
              <li>age of retirement</li>
              <li>life expectancy</li>
            </ul>
          </p>

          <a href="#/dashboard" className="backtodash-link">
            <i className="fa fa-fw fa-arrow-left" />
            &nbsp;Back to Dashboard
          </a>
        </section>
      )
    }
  }

  private getExpenseFor(index: number, groupName: ExpenseCalculators.ExpenseGroups): string {
    if (index >= this.state.expensesByGroupAndDate.length) { return '' }
    let v = 0
    if (_.has(this.state.expensesByGroupAndDate[index], groupName)) {
      v = this.state.expensesByGroupAndDate[index][groupName]
    }
    return (0 === v) ? '-' : utils.toCurrency(v, true)
  }
}

const ExpensesSummary = () => {
  return (
    <ExpensesSummaryContainer
      header="Expenses Summary"
      icon="fa fa-fw fa-balance-scale" />
  )
}

export {
  ExpensesSummary
}



// WEBPACK FOOTER //
// ./src/app/containers/PipelineSection/Expenses/Summary/index.tsx